import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { Redirect } from "react-router";
import { StringParam, useQueryParam } from "use-query-params";
import apiFetch, { dumpErrorOnCustomer, ensureStatusOk } from "../util/fetch-utils";
import fetchLoginStatus from "../util/login-status";
import UserSelectedPlan from "../Auth/UserSelectedPlan";
import { usePriceAmount } from "../AccountDetails/Subscription/subscriptionUtils";
import { BILLING_API_BASE_PATH, useStripePromise } from "./stripeClient";

const spin = keyframes`
    to { transform: rotate(360deg); }
`;

const Wrap = styled.div`
    max-width: 480px;
    margin: 60px auto;
    text-align: center;
    font-family: "Inter", system-ui, sans-serif;
    padding: 0 24px;
`;

const Title = styled.h1`
    font-size: clamp(1.6rem, 4vw, 2rem);
    font-weight: 800;
    color: #0f172a;
    margin: 0 0 12px;
    letter-spacing: -0.5px;
`;

const Sub = styled.p`
    font-size: 0.95rem;
    color: #64748b;
    margin: 0 0 32px;
    line-height: 1.6;
`;

const Spinner = styled.div`
    width: 40px;
    height: 40px;
    border: 3px solid #e2e8f0;
    border-top-color: #6366f1;
    border-radius: 50%;
    animation: ${spin} 0.8s linear infinite;
    margin: 0 auto;
`;

const PlanWrap = styled.div`
    display: flex;
    justify-content: center;
    margin-bottom: 28px;
`;

const ConfirmBtn = styled.button`
    width: 100%;
    padding: 13px;
    background: #6366f1;
    color: #fff;
    border: none;
    border-radius: 10px;
    font-size: 1rem;
    font-weight: 700;
    font-family: "Inter", system-ui, sans-serif;
    cursor: pointer;
    transition: background 120ms;
    &:hover { background: #4f46e5; }
`;

const FREE_PRODUCT_SESSION_ID = "free-product";

const Checkout = () => {
    const [sessionId, setSessionId] = useState("");
    const [redirect, setRedirect]   = useState("");
    const login      = useSelector(s => s.login);
    const isLoggedIn = useSelector(s => s.login.userDetails.isLoggedIn);
    const dispatch   = useDispatch();
    const [priceId]  = useQueryParam('priceId', StringParam);
    const priceAmount = usePriceAmount(priceId);
    const stripePromise = useStripePromise();

    useEffect(() => {
        if (!login.userDetails.fetchAttempted) dispatch(fetchLoginStatus(login));
        if (isLoggedIn && priceAmount !== undefined) {
            if (priceAmount > 0) createCheckoutSession(priceId);
            else setSessionId(FREE_PRODUCT_SESSION_ID);
        }
    }, [priceId, login, priceAmount, dispatch, isLoggedIn]);

    if (redirect) return <Redirect to={redirect} />;

    if (login.userDetails.fetchAttempted && !isLoggedIn) {
        setRedirect("/register?priceId=" + priceId);
    }

    return (
        <Wrap>
            {!sessionId ? <CreatingPlan /> : <PlanCreated />}
        </Wrap>
    );

    function CreatingPlan() {
        return (
            <>
                <Title>Setting up your plan…</Title>
                <Sub>We're provisioning your resources. This only takes a moment.</Sub>
                <Spinner />
            </>
        );
    }

    function PlanCreated() {
        return (
            <>
                <Title>Your plan is ready 🚀</Title>
                <Sub>
                    {priceAmount > 0
                        ? "Review your selection and proceed to secure payment."
                        : "Confirm below to activate your free plan."}
                </Sub>
                <PlanWrap>
                    <UserSelectedPlan standalone />
                </PlanWrap>
                <ConfirmBtn onClick={proceed} data-test="confirm-plan-button">
                    {priceAmount > 0 ? "Proceed to payment" : "Confirm free plan"}
                </ConfirmBtn>
            </>
        );
    }

    function proceed() {
        if (priceAmount > 0) {
            stripePromise.then(stripe => stripe.redirectToCheckout({ sessionId }));
        } else {
            apiFetch(BILLING_API_BASE_PATH + "/subscribe-to-free", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: priceId,
            })
                .then(ensureStatusOk)
                .then(() => setRedirect("/account/subscription/?changeResult=success"))
                .catch(dumpErrorOnCustomer);
        }
    }

    function createCheckoutSession(priceId) {
        apiFetch(BILLING_API_BASE_PATH + "/create-checkout-session", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: priceId,
        })
            .then(ensureStatusOk)
            .then(r => r.text())
            .then(id => setSessionId(id))
            .catch(err => console.error("Checkout session error", err));
    }
};

export default Checkout;
